import { useCallback } from 'react';

import {
  BaseInfiniteScroll,
  type ICheckBoxGroupProps,
  IPaginationMetaProps,
} from '@gbs-monorepo-packages/common';

import { type IFont } from '../../../../pages/EditCourse/components/FontsModal';
import { filterByAttribute } from '../../../../utils/filters';
import {
  CardsContainer,
  CheckBoxCardContainer,
  Container,
  FontCardCustom,
  LabelNoFonts,
} from './styles';

export interface ISelectTemplatesProps
  extends Omit<
    ICheckBoxGroupProps,
    'children' | 'defaultValue' | 'onValueChange' | 'type' | 'value'
  > {
  dataCy?: string;
  fonts: IFont[];
  name?: string;
  onValueChange?: (value: IFont) => void;
  placeholder?: string;
  value: IFont[];
  search: string;
  paginationMeta?: IPaginationMetaProps;
  searchFonts: () => void;
  loading: boolean;
}

export const SelectFonts = ({
  fonts,
  onValueChange,
  value: dataSelected,
  search,
  paginationMeta,
  searchFonts,
  loading,
  ...props
}: ISelectTemplatesProps): JSX.Element => {
  const dataFiltered = filterByAttribute(fonts, 'family', search);
  const dataSelectedFiltered = filterByAttribute(
    dataSelected,
    'family',
    search
  );

  const handleCheck = useCallback(
    (newChecked: IFont) => {
      onValueChange?.(newChecked);
    },
    [onValueChange]
  );

  const handleUncheck = useCallback(
    (newChecked: IFont) => {
      onValueChange?.(newChecked);
    },
    [onValueChange]
  );

  return (
    <Container
      {...props}
      type="multiple"
      data-cy="select-fonts-container"
      defaultValue={dataSelected.map((data) => data.family)}
    >
      <CardsContainer data-cy="fontsModal-container" selectedArea={false}>
        {!dataFiltered.length && !loading ? (
          <LabelNoFonts data-cy="text-noFonts">No fonts found</LabelNoFonts>
        ) : (
          <BaseInfiniteScroll
            dataLength={dataFiltered.length}
            hasMore={dataFiltered.length < (paginationMeta?.total ?? 0)}
            next={searchFonts}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flexBasis: 'auto',
              flexGrow: 0,
            }}
            loading={loading}
            endMessage={'You have seen it all'}
            dataCy="fonts"
          >
            {dataFiltered.map((data) => (
              <FontCardCustom
                data={data}
                key={data.family}
                disabled={data.selected}
                defaultChecked={data.selected || loading}
                onClick={() => {
                  handleCheck(data);
                }}
                isNotButton
                data-state={data.selected === true ? 'on' : 'off'}
              />
            ))}
          </BaseInfiniteScroll>
        )}
      </CardsContainer>
      <CardsContainer data-cy="fontsModal-selected-container" selectedArea>
        <CheckBoxCardContainer>
          {dataSelectedFiltered.map((data) => (
            <FontCardCustom
              key={`${data.index}-selected`}
              data={data}
              onClick={() => {
                handleUncheck(data);
              }}
              disabled={loading}
              defaultChecked={data.selected}
              isNotButton
              data-state={data.selected === true ? 'on' : 'off'}
            />
          ))}
        </CheckBoxCardContainer>
      </CardsContainer>
    </Container>
  );
};
